import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getGithubRepositories,
  getGithubDashboard,
} from '../../shared/api/api';
import './GithubPrsPage.css';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function reviewStateClass(state) {
  if (!state) return 'pending';
  const s = state.toUpperCase();
  if (s === 'APPROVED') return 'approved';
  if (s === 'CHANGES_REQUESTED') return 'changes';
  if (s === 'COMMENTED') return 'commented';
  return 'pending';
}

function reviewStateLabel(state) {
  if (!state) return 'Pending';
  const s = state.toUpperCase();
  if (s === 'APPROVED') return 'Approved';
  if (s === 'CHANGES_REQUESTED') return 'Changes Requested';
  if (s === 'COMMENTED') return 'Commented';
  return 'Pending';
}

function deployStateClass(state) {
  if (!state) return 'unknown';
  const s = state.toLowerCase();
  if (s === 'success') return 'success';
  if (s === 'failure' || s === 'error') return 'failure';
  if (s === 'in_progress') return 'progress';
  return 'pending';
}

/* ── PR card in the content area ─────────────────────────────────────────── */
function PrCard({ pr }) {
  const reviewCls = reviewStateClass(pr.reviewState);
  return (
    <div className="gpr-pr-card">
      <div className="gpr-pr-card-top">
        <div className="gpr-pr-meta">
          <span className="gpr-pr-number">#{pr.number}</span>
          {pr.draft && <span className="gpr-badge gpr-badge--draft">Draft</span>}
          {pr.merged && <span className="gpr-badge gpr-badge--merged">Merged</span>}
          <span className={`gpr-badge gpr-badge--review gpr-badge--${reviewCls}`}>
            {reviewStateLabel(pr.reviewState)}
          </span>
        </div>
        <span className="gpr-pr-time">{timeAgo(pr.updatedAt || pr.createdAt)}</span>
      </div>

      <a className="gpr-pr-title" href={pr.htmlUrl} target="_blank" rel="noopener noreferrer">
        {pr.title}
      </a>

      <div className="gpr-pr-branches">
        <code className="gpr-branch">{pr.headBranch}</code>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        <code className="gpr-branch">{pr.baseBranch}</code>
      </div>

      <div className="gpr-pr-footer">
        <div className="gpr-pr-author">
          <div className="gpr-author-avatar">{(pr.author || '?').slice(0, 2).toUpperCase()}</div>
          <span>{pr.author}</span>
        </div>
        <div className="gpr-pr-counts">
          <span title="Comments">💬 {pr.commentsCount || 0}</span>
          <span title="Review comments">📝 {pr.reviewCommentsCount || 0}</span>
        </div>
      </div>

      {((pr.approvedBy?.length > 0) || (pr.requestedChangesBy?.length > 0)) && (
        <div className="gpr-review-summary">
          {pr.approvedBy?.length > 0 && (
            <span className="gpr-review-approved">✓ {pr.approvedBy.join(', ')}</span>
          )}
          {pr.requestedChangesBy?.length > 0 && (
            <span className="gpr-review-changes">✗ {pr.requestedChangesBy.join(', ')}</span>
          )}
        </div>
      )}

      {pr.latestDeployment && (
        <div className={`gpr-deploy-strip gpr-deploy-strip--${deployStateClass(pr.latestDeployment.deploymentState)}`}>
          <span className="gpr-deploy-dot" />
          <span className="gpr-deploy-env">{pr.latestDeployment.environment}</span>
          <span className="gpr-deploy-state">{pr.latestDeployment.deploymentState}</span>
          <span className="gpr-deploy-time">{timeAgo(pr.latestDeployment.deployedAt)}</span>
        </div>
      )}
    </div>
  );
}

/* ── Deployment row in the content area ──────────────────────────────────── */
function DeploymentRow({ dep }) {
  const cls = deployStateClass(dep.deploymentState);
  return (
    <div className={`gpr-dep-row gpr-dep-row--${cls}`}>
      <div className={`gpr-dep-dot gpr-dep-dot--${cls}`} />
      <div className="gpr-dep-body">
        <div className="gpr-dep-env">
          {dep.environment}
          {dep.prNumber && <span className="gpr-dep-pr"> · #{dep.prNumber} {dep.prTitle}</span>}
        </div>
        <div className="gpr-dep-branch">{dep.branch}{dep.description ? ` — ${dep.description}` : ''}</div>
        {dep.statusHistory?.length > 0 && (
          <div className="gpr-dep-history">{dep.statusHistory[0]}</div>
        )}
      </div>
      <div className="gpr-dep-right">
        <span className={`gpr-dep-state gpr-dep-state--${cls}`}>{dep.deploymentState}</span>
        <span className="gpr-dep-meta">by {dep.deployedBy || '—'} · {timeAgo(dep.deployedAt)}</span>
      </div>
      {dep.logUrl && (
        <a href={dep.logUrl} target="_blank" rel="noopener noreferrer" className="gpr-dep-log">Logs →</a>
      )}
    </div>
  );
}

/* ── Repo content panel (tabs + PR/deploy list) ───────────────────────────── */
function RepoContent({ repoData, fullName }) {
  const { pendingPullRequests = [], approvedPullRequests = [], recentDeployments = [] } = repoData;
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');

  const allPrs = useMemo(() => {
    const map = new Map();
    [...pendingPullRequests, ...approvedPullRequests].forEach(pr => map.set(pr.number, pr));
    return [...map.values()];
  }, [pendingPullRequests, approvedPullRequests]);

  const rawList = tab === 'pending'  ? pendingPullRequests
               : tab === 'approved' ? approvedPullRequests
               : tab === 'all'      ? allPrs
               : recentDeployments;

  const filtered = useMemo(() => {
    if (!search.trim()) return rawList;
    const q = search.toLowerCase();
    if (tab === 'deploys') {
      return rawList.filter(d =>
        d.environment?.toLowerCase().includes(q) ||
        d.branch?.toLowerCase().includes(q) ||
        d.deployedBy?.toLowerCase().includes(q) ||
        d.prTitle?.toLowerCase().includes(q)
      );
    }
    return rawList.filter(pr =>
      pr.title?.toLowerCase().includes(q) ||
      pr.author?.toLowerCase().includes(q) ||
      String(pr.number).includes(q) ||
      pr.headBranch?.toLowerCase().includes(q)
    );
  }, [rawList, search, tab]);

  const tabs = [
    { key: 'pending',  label: 'Pending',    count: pendingPullRequests.length },
    { key: 'approved', label: 'Approved',   count: approvedPullRequests.length },
    { key: 'all',      label: 'All PRs',    count: allPrs.length },
    { key: 'deploys',  label: 'Deployments',count: recentDeployments.length },
  ];

  return (
    <div className="gpr-repo-content">
      {/* Repo title */}
      <div className="gpr-content-header">
        <div className="gpr-content-repo-name">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
          {fullName.includes('/')
            ? <span>{fullName.split('/')[0]} / <strong>{fullName.split('/')[1]}</strong></span>
            : <strong>{fullName}</strong>}
        </div>
        <div className="gpr-content-stats">
          <span className="gpr-stat-chip gpr-stat-chip--pending">{pendingPullRequests.length} pending</span>
          <span className="gpr-stat-chip gpr-stat-chip--approved">{approvedPullRequests.length} approved</span>
          <span className="gpr-stat-chip gpr-stat-chip--deploy">{recentDeployments.length} deploys</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="gpr-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`gpr-tab${tab === t.key ? ' gpr-tab--active' : ''}`}
            onClick={() => { setTab(t.key); setSearch(''); }}
          >
            {t.label}
            <span className="gpr-tab-count">{t.count}</span>
          </button>
        ))}

        {/* Search inline with tabs */}
        <div className="gpr-tabs-search">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input
            placeholder={tab === 'deploys' ? 'Search deployments…' : 'Search PRs…'}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && <button onClick={() => setSearch('')}>✕</button>}
        </div>
      </div>

      {/* Content */}
      <div className="gpr-content-body">
        {tab === 'deploys' ? (
          <div className="gpr-dep-list">
            {filtered.length === 0
              ? <p className="gpr-empty">No recent deployments.</p>
              : filtered.map((dep, i) => <DeploymentRow key={dep.deploymentId || i} dep={dep} />)
            }
          </div>
        ) : (
          <div className="gpr-pr-grid">
            {filtered.length === 0
              ? <p className="gpr-empty">No pull requests found.</p>
              : filtered.map(pr => <PrCard key={pr.number} pr={pr} />)
            }
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────────── */
function GithubPrsPage() {
  const navigate = useNavigate();
  const [repos, setRepos] = useState([]);
  const [dashboard, setDashboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRepo, setSelectedRepo] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [repoRes, dashRes] = await Promise.all([
        getGithubRepositories(),
        getGithubDashboard(),
      ]);
      const repoList = Array.isArray(repoRes.data) ? repoRes.data : [];
      const dashList = Array.isArray(dashRes.data) ? dashRes.data : [];
      setRepos(repoList);
      setDashboard(dashList);
      if (repoList.length > 0 && !selectedRepo) {
        setSelectedRepo(repoList[0].fullName);
      }
    } catch {
      setError('Failed to load GitHub data. Make sure the backend is configured with GitHub credentials.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const totalPending  = useMemo(() => dashboard.reduce((s, d) => s + (d.pendingPullRequests?.length || 0), 0), [dashboard]);
  const totalApproved = useMemo(() => dashboard.reduce((s, d) => s + (d.approvedPullRequests?.length || 0), 0), [dashboard]);
  const totalDeploys  = useMemo(() => dashboard.reduce((s, d) => s + (d.recentDeployments?.length || 0), 0), [dashboard]);

  const activeRepoData = useMemo(() => {
    if (!selectedRepo) return null;
    return dashboard.find(d => {
      const matched = repos.find(r => r.repo === d.repo);
      return (matched?.fullName || d.repo) === selectedRepo;
    });
  }, [selectedRepo, dashboard, repos]);

  const activeFullName = selectedRepo || '';

  return (
    <div className="gpr-shell">
      <div className="gpr-main">

        {/* Page header */}
        <div className="gpr-page-header">
          <div>
            <h1 className="gpr-page-title">GitHub Pull Requests</h1>
            <p className="gpr-page-sub">{repos.length} monitored {repos.length === 1 ? 'repository' : 'repositories'}</p>
          </div>
          <div className="gpr-page-header-right">
            <div className="gpr-kpi">
              <span className="gpr-kpi-val gpr-kpi-val--pending">{totalPending}</span>
              <span className="gpr-kpi-lbl">Pending</span>
            </div>
            <div className="gpr-kpi">
              <span className="gpr-kpi-val gpr-kpi-val--approved">{totalApproved}</span>
              <span className="gpr-kpi-lbl">Approved</span>
            </div>
            <div className="gpr-kpi">
              <span className="gpr-kpi-val gpr-kpi-val--deploy">{totalDeploys}</span>
              <span className="gpr-kpi-lbl">Deploys</span>
            </div>
            <button className="gpr-refresh" onClick={fetchAll}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.02-6.63"/></svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Split: repo list left | content right */}
        <div className="gpr-split">

          {/* ── REPO SIDEBAR ── */}
          <div className="gpr-repo-sidebar">
            <div className="gpr-repo-sidebar-label">Repositories</div>

            {loading && (
              <div className="gpr-loading">
                <div className="gpr-spinner" />
              </div>
            )}
            {error && <div className="gpr-error">{error}</div>}

            {!loading && !error && dashboard.map(repoData => {
              const matched = repos.find(r => r.repo === repoData.repo);
              const fullName = matched?.fullName || repoData.repo;
              const repoName = fullName.includes('/') ? fullName.split('/')[1] : fullName;
              const org      = fullName.includes('/') ? fullName.split('/')[0] : null;
              const pending  = repoData.pendingPullRequests?.length || 0;
              const deploys  = repoData.recentDeployments?.length || 0;
              const isActive = selectedRepo === fullName;

              return (
                <div
                  key={fullName}
                  className={`gpr-repo-card${isActive ? ' gpr-repo-card--active' : ''}`}
                  onClick={() => setSelectedRepo(fullName)}
                >
                  <div className="gpr-repo-card-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
                  </div>
                  <div className="gpr-repo-card-body">
                    {org && <div className="gpr-repo-card-org">{org}</div>}
                    <div className="gpr-repo-card-name">{repoName}</div>
                    <div className="gpr-repo-card-chips">
                      {pending > 0 && <span className="gpr-chip gpr-chip--pending">{pending} pending</span>}
                      {deploys > 0 && <span className="gpr-chip gpr-chip--deploy">{deploys} deploys</span>}
                    </div>
                  </div>
                  {isActive && (
                    <svg className="gpr-repo-card-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                  )}
                </div>
              );
            })}

            {!loading && !error && dashboard.length === 0 && (
              <div className="gpr-repo-empty">
                <p>No repositories found.</p>
                <p>Check <code>GITHUB_MONITORED_REPOS</code> in the backend config.</p>
              </div>
            )}
          </div>

          {/* ── CONTENT AREA ── */}
          <div className="gpr-content-area">
            {loading ? (
              <div className="gpr-content-placeholder">
                <div className="gpr-spinner" />
                <span>Loading…</span>
              </div>
            ) : !activeRepoData ? (
              <div className="gpr-content-placeholder">
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>
                <p>Select a repository to view its pull requests</p>
              </div>
            ) : (
              <RepoContent
                key={activeFullName}
                repoData={activeRepoData}
                fullName={activeFullName}
              />
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default GithubPrsPage;

